// Public list of live-in-directory therapists, sourced from
// therapist_applications. Uses supabaseAdmin server-side to whitelist ONLY
// safe, public-facing columns (no email, license number, insurance data, etc).

import { createServerFn } from "@tanstack/react-start";
import type { Therapist, SessionType, Availability } from "@/lib/therapists-data";

export type PublishedTherapistRow = {
  id: string;
  full_name: string | null;
  professional_title: string | null;
  short_bio: string | null;
  country: string | null;
  state_region: string | null;
  licensed_country: string | null;
  licensed_states: string[] | null;
  therapy_approaches: string[] | null;
  specialisms: string[] | null;
  languages: string[] | null;
  availability_modes: string[] | null;
  photo_url: string | null;
  verified_badge: boolean | null;
  years_experience: number | null;
  website: string | null;
  // NOTE: email is deliberately never selected or exposed publicly.
  additional_jurisdictions: Array<{ country?: string; states?: string[] }> | null;
};

export const listPublishedTherapists = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublishedTherapistRow[]> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("therapist_applications")
      .select(
        "id, full_name, professional_title, short_bio, country, state_region, licensed_country, licensed_states, therapy_approaches, specialisms, languages, availability_modes, photo_url, verified_badge, years_experience, website, additional_jurisdictions",
      )
      .eq("status", "live_in_directory")
      .eq("consent_to_list", true);
    if (error) throw new Error(error.message);
    return (data ?? []) as PublishedTherapistRow[];
  },
);

// Map a DB row into the Therapist shape used by the directory UI.
export function mapPublishedRowToTherapist(row: PublishedTherapistRow): Therapist {
  const modalities = row.therapy_approaches ?? [];
  const specialties = row.specialisms ?? [];
  const languages = row.languages ?? [];
  const modes = (row.availability_modes ?? []).map((m) => m.toLowerCase());
  const formats: SessionType[] = [];
  if (modes.some((m) => m.includes("online") || m.includes("tele") || m.includes("video"))) {
    formats.push("Online");
  }
  if (modes.some((m) => m.includes("in-person") || m.includes("in person") || m.includes("office"))) {
    formats.push("In-person");
  }
  if (modes.some((m) => m.includes("phone"))) formats.push("Phone");
  if (modes.some((m) => m.includes("group"))) formats.push("Group");
  if (formats.length === 0) formats.push("Online");

  const country = row.licensed_country ?? row.country ?? "";
  const region = row.state_region ?? (row.licensed_states?.[0] ?? "");
  const location = [region, country].filter(Boolean).join(", ");

  return {
    id: row.id,
    name: row.full_name ?? "Therapist",
    credentials: row.professional_title ?? "",
    title: row.professional_title ?? "Therapist",
    bio: row.short_bio ?? "",
    location: location || "—",
    country,
    yearsExperience: row.years_experience ?? 0,
    verified: !!row.verified_badge,
    contactEmail: "",
    modalities,
    specialties,
    issues: specialties,
    formats,
    availability: ["Taking new clients"] as Availability[],
    languages: languages.length ? languages : ["English"],
    feeRange: "$$",
    acceptingClients: true,
    profileUrl: row.website ?? "",
    photo: row.photo_url ?? undefined,
    cartoonSeed: row.id,
    website: row.website ?? undefined,
    licensedStates: row.licensed_states ?? [],
    licensingJurisdiction: country,
  };
}
